import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { FloatButton, Tooltip, message as antdMessage } from 'antd';
import type { ChatMessage, ChatSession, ChatStreamEvent, TicketProposal } from '@nxsys/types/models/AiChat';
import { useAiChatStream } from '@nxsys/hooks/useAiChatStream';
import { getSessions, getSession, deleteSession, getChatConfig, getDailyGreeting, createSupportTicket, SupportTicketRefusedError } from '@nxsys/services/api/ai';
import type { SuggestionChip } from './ChatWindow';
import ChatWindow from './ChatWindow';
import { parseTicketProposal } from './ticketProposal';
import { LogoRenderer, logoStyles } from './LogoRenderer';
import type { LogoKey } from './LogoRenderer';

const LOGO_STORAGE_KEY = 'ai-chatbot-logo';
const PRIMARY_COLOR_STORAGE_KEY = 'ai-chatbot-primary-color';
const IS_OPEN_KEY = 'ai-chatbot-is-open';
const DAILY_GREETING_KEY = 'ai-chatbot-daily-greeting'; // { date, greeting, suggestions }

const SEGMENT_DELIMITER = '\n<!-- SEGMENT -->\n';

/**
 * Capture a real, pixel-accurate screenshot via the browser screen-capture API.
 * Prompts the user to pick a tab/window/screen, grabs one frame, then stops.
 * Returns undefined if unsupported or the user cancels.
 */
async function captureScreen(): Promise<string | undefined> {
  const md = navigator.mediaDevices as MediaDevices | undefined;
  if (!md?.getDisplayMedia) return undefined;
  let stream: MediaStream | undefined;
  try {
    stream = await md.getDisplayMedia({
      // Pre-select the current tab to reduce friction (Chromium-only hint).
      preferCurrentTab: true,
      video: true,
      audio: false,
    } as MediaStreamConstraints);
    const video = document.createElement('video');
    video.srcObject = stream;
    await video.play();
    // Wait one frame so dimensions/content are ready.
    await new Promise((r) => requestAnimationFrame(() => r(null)));
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx || !canvas.width) return undefined;
    ctx.drawImage(video, 0, 0);
    return canvas.toDataURL('image/jpeg', 0.85);
  } catch {
    // Unsupported or user cancelled the share dialog — proceed without a shot.
    return undefined;
  } finally {
    stream?.getTracks().forEach((t) => t.stop());
  }
}

/** Split assistant messages that contain segment delimiters into multiple bubbles. */
function expandSegments(messages: ChatMessage[]): ChatMessage[] {
  const result: ChatMessage[] = [];
  for (const msg of messages) {
    if (msg.role === 'assistant' && msg.content.includes(SEGMENT_DELIMITER)) {
      const segments = msg.content.split(SEGMENT_DELIMITER).filter((s) => s.trim());
      segments.forEach((segment, i) => {
        result.push({
          ...msg,
          id: `${msg.id}-seg-${i}`,
          content: segment,
        });
      });
    } else {
      result.push(msg);
    }
  }
  return result;
}

type ChatState = {
  isOpen: boolean;
  sessionId: string | undefined;
  messages: ChatMessage[];
  sessions: ChatSession[];
  activeToolCall: { name: string; label: string } | null;
  showSessionList: boolean;
  title: string;
  placeholder: string;
  greeting: string;
  suggestions: SuggestionChip[];
  quickActions: SuggestionChip[];
  suggestFeedback: boolean;
};

type Action =
  | { type: 'TOGGLE_OPEN' }
  | { type: 'CLOSE' }
  | { type: 'SET_SESSIONS'; payload: ChatSession[] }
  | { type: 'ADD_USER_MESSAGE'; payload: ChatMessage }
  | { type: 'ADD_ASSISTANT_MESSAGE'; payload: ChatMessage }
  | { type: 'APPEND_TOKEN'; payload: string }
  | { type: 'SET_TOOL_CALL'; payload: { name: string; label: string } | null }
  | { type: 'SET_SESSION_ID'; payload: string }
  | { type: 'LOAD_SESSION'; payload: { sessionId: string; messages: ChatMessage[] } }
  | { type: 'NEW_CHAT' }
  | { type: 'DELETE_SESSION'; payload: string }
  | { type: 'TOGGLE_SESSION_LIST' }
  | { type: 'SET_CHAT_CONFIG'; payload: { title: string; placeholder: string; greeting: string; suggestions: SuggestionChip[] } }
  | { type: 'SET_QUICK_ACTIONS'; payload: SuggestionChip[] }
  | { type: 'REPLACE_ASSISTANT_SEGMENTS'; payload: string }
  | { type: 'CLEANUP_EMPTY_ASSISTANTS' }
  | { type: 'SET_SUGGEST_FEEDBACK'; payload: boolean }
  | { type: 'OPEN' }
;

// On a new day, start with chatbox closed (daily greeting will auto-open it)
// Only restore persisted open state if same day
const getInitialIsOpen = (): boolean => {
  try {
    const cached = JSON.parse(localStorage.getItem(DAILY_GREETING_KEY) || 'null');
    if (cached?.date === getTodayString()) {
      return localStorage.getItem(IS_OPEN_KEY) === 'true';
    }
  } catch { /* ignore */ }
  return false;
};

const initialState: ChatState = {
  isOpen: getInitialIsOpen(),
  sessionId: undefined,
  messages: [],
  sessions: [],
  activeToolCall: null,
  showSessionList: false,
  title: 'AI Assistant',
  placeholder: 'Ask about payroll, taxes, employees...',
  greeting: '',
  suggestions: [],
  quickActions: [],
  suggestFeedback: false,
};

function reducer(state: ChatState, action: Action): ChatState {
  switch (action.type) {
    case 'TOGGLE_OPEN':
      return { ...state, isOpen: !state.isOpen, showSessionList: false };
    case 'CLOSE':
      return { ...state, isOpen: false, showSessionList: false };
    case 'SET_SESSIONS':
      return { ...state, sessions: action.payload };
    case 'ADD_USER_MESSAGE':
      return { ...state, messages: [...state.messages, action.payload], quickActions: [], suggestFeedback: false };
    case 'ADD_ASSISTANT_MESSAGE':
      return { ...state, messages: [...state.messages, action.payload] };
    case 'APPEND_TOKEN': {
      const msgs = [...state.messages];
      if (msgs.length === 0) return state;
      const last = msgs[msgs.length - 1];
      if (last.role !== 'assistant') return state;
      msgs[msgs.length - 1] = { ...last, content: last.content + action.payload };
      return { ...state, messages: msgs };
    }
    case 'SET_TOOL_CALL':
      return { ...state, activeToolCall: action.payload };
    case 'SET_SESSION_ID':
      return { ...state, sessionId: action.payload };
    case 'LOAD_SESSION':
      return {
        ...state,
        sessionId: action.payload.sessionId,
        messages: action.payload.messages,
        showSessionList: false,
        suggestFeedback: false,
      };
    case 'NEW_CHAT':
      return {
        ...state,
        sessionId: undefined,
        messages: [],
        showSessionList: false,
        quickActions: [],
        suggestFeedback: false,
      };
    case 'DELETE_SESSION': {
      const sessions = state.sessions.filter((s) => s.id !== action.payload);
      const isActive = state.sessionId === action.payload;
      const sessionId = isActive ? undefined : state.sessionId;
      const messages = isActive ? [] : state.messages;
      return { ...state, sessions, sessionId, messages, suggestFeedback: isActive ? false : state.suggestFeedback };
    }
    case 'TOGGLE_SESSION_LIST':
      return { ...state, showSessionList: !state.showSessionList };
    case 'SET_CHAT_CONFIG':
      return {
        ...state,
        title: action.payload.title,
        placeholder: action.payload.placeholder,
        greeting: action.payload.greeting,
        suggestions: action.payload.suggestions,
      };
    case 'SET_QUICK_ACTIONS':
      return { ...state, quickActions: action.payload };
    case 'REPLACE_ASSISTANT_SEGMENTS': {
      const segments = action.payload.includes(SEGMENT_DELIMITER)
        ? action.payload.split(SEGMENT_DELIMITER).filter((s: string) => s.trim())
        : [action.payload];
      // Find index of last user message — all assistant messages after it are from this turn
      const lastUserIdx = state.messages.map((m) => m.role).lastIndexOf('user');
      const prefix = state.messages.slice(0, lastUserIdx + 1);
      // Reuse the streaming bubbles' ids so React reconciles in place (no remount).
      // Remounting would reset embed/iframe state and flash the whole message.
      const streamingAssistants = state.messages
        .slice(lastUserIdx + 1)
        .filter((m) => m.role === 'assistant');
      const now = new Date().toISOString();
      const segmentMessages: ChatMessage[] = segments.map((content: string, i: number) => ({
        id: streamingAssistants[i]?.id ?? `assistant-final-${Date.now()}-${i}`,
        sessionId: state.sessionId || '',
        role: 'assistant' as const,
        content,
        createdAt: streamingAssistants[i]?.createdAt ?? now,
      }));
      return { ...state, messages: [...prefix, ...segmentMessages] };
    }
    case 'CLEANUP_EMPTY_ASSISTANTS':
      return { ...state, messages: state.messages.filter((m) => m.content.trim() !== '' || m.role !== 'assistant') };
    case 'SET_SUGGEST_FEEDBACK':
      return { ...state, suggestFeedback: action.payload };
    case 'OPEN':
      return { ...state, isOpen: true, showSessionList: false };
    default:
      return state;
  }
}

const getTodayString = () => new Date().toISOString().slice(0, 10);

const AiChatbot: React.FC = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { sendMessage, isStreaming, abort } = useAiChatStream();
  const [configLoaded, setConfigLoaded] = useState(false);
  const [selectedLogo, setSelectedLogo] = useState<LogoKey>(() => {
    const stored = localStorage.getItem(LOGO_STORAGE_KEY);
    const validKeys: LogoKey[] = ['default', 'gradient-orb', 'mesh-blob', 'glass-ring', 'starburst', 'glass-panel', 'aurora', 'dot-matrix', 'saturn'];
    return stored && validKeys.includes(stored as LogoKey) ? (stored as LogoKey) : 'glass-ring';
  });
  const [usePrimaryColor, setUsePrimaryColor] = useState<boolean>(
    () => {
      const stored = localStorage.getItem(PRIMARY_COLOR_STORAGE_KEY);
      return stored !== null ? stored === 'true' : true;
    },
  );

  // Item 1: bounce + auto tooltip when chatbox is closed
  const [showBounce, setShowBounce] = useState(false);
  const [showAutoTooltip, setShowAutoTooltip] = useState(false);

  // Item 2: daily greeting streaming
  const [streamingGreetingText, setStreamingGreetingText] = useState('');
  const [isGreetingStreaming, setIsGreetingStreaming] = useState(false);
  const greetingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Load chat config on mount — hide button entirely if it fails
  useEffect(() => {
    getChatConfig()
      .then((config) => {
        dispatch({ type: 'SET_CHAT_CONFIG', payload: config });
        setConfigLoaded(true);
      })
      .catch(() => {
        // Config failed to load — don't show the chatbot
      });
  }, []);

  // Item 1: Bounce + auto tooltip when chatbox is closed
  useEffect(() => {
    const isCurrentlyOpen = localStorage.getItem(IS_OPEN_KEY) === 'true';
    if (isCurrentlyOpen) return;

    setShowBounce(true);
    const bounceOff = setTimeout(() => setShowBounce(false), 10000);
    const tooltipOn = setTimeout(() => setShowAutoTooltip(true), 1500);
    const tooltipOff = setTimeout(() => setShowAutoTooltip(false), 7000);
    return () => {
      clearTimeout(bounceOff);
      clearTimeout(tooltipOn);
      clearTimeout(tooltipOff);
    };
  }, []);

  // Item 2: Daily greeting
  // First load of the day: auto-open, call API, stream greeting, cache to localStorage
  // Subsequent loads same day: read cached greeting, apply immediately
  // Suggestions always come from getChatConfig (static), not from daily greeting
  useEffect(() => {
    if (!configLoaded) return;
    const today = getTodayString();

    // Check cached daily greeting
    let cached: { date?: string; greeting?: string } | null = null;
    try { cached = JSON.parse(localStorage.getItem(DAILY_GREETING_KEY) || 'null'); } catch { /* ignore */ }

    // Same day — apply cached greeting immediately (suggestions from getChatConfig)
    if (cached?.date === today && cached?.greeting) {
      dispatch({ type: 'SET_CHAT_CONFIG', payload: {
        title: state.title,
        placeholder: state.placeholder,
        greeting: cached.greeting,
        suggestions: state.suggestions,
      }});
      return;
    }

    // New day — fetch greeting + wait minimum delay in parallel, then auto-open
    let cancelled = false;

    if (state.sessions.length === 0) {
      getSessions()
        .then((sessions) => dispatch({ type: 'SET_SESSIONS', payload: sessions }))
        .catch(() => dispatch({ type: 'SET_SESSIONS', payload: [] }));
    }

    const greetingPromise = getDailyGreeting()
      .then((result) => result.greeting)
      .catch(() => null);
    const delayPromise = new Promise<void>((r) => setTimeout(r, 2500));

    Promise.all([greetingPromise, delayPromise]).then(([fullGreeting]) => {
      if (cancelled) return;

      if (fullGreeting) {
        // Cache date + greeting
        localStorage.setItem(DAILY_GREETING_KEY, JSON.stringify({
          date: today,
          greeting: fullGreeting,
        }));
      }

      // Open chatbox only after both greeting + delay are ready
      localStorage.setItem(IS_OPEN_KEY, 'true');
      dispatch({ type: 'OPEN' });

      if (fullGreeting) {
        // Stream greeting character by character
        setIsGreetingStreaming(true);
        setStreamingGreetingText('');
        let idx = 0;
        greetingIntervalRef.current = setInterval(() => {
          idx++;
          setStreamingGreetingText(fullGreeting.slice(0, idx));
          if (idx >= fullGreeting.length) {
            if (greetingIntervalRef.current) clearInterval(greetingIntervalRef.current);
            setIsGreetingStreaming(false);
            dispatch({ type: 'SET_CHAT_CONFIG', payload: {
              title: state.title,
              placeholder: state.placeholder,
              greeting: fullGreeting,
              suggestions: state.suggestions,
            }});
          }
        }, 25);
      }
    });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configLoaded]);

  // Cleanup streaming interval on unmount
  useEffect(() => {
    return () => {
      if (greetingIntervalRef.current) clearInterval(greetingIntervalRef.current);
    };
  }, []);

  // Item 4: Load sessions on mount if chatbox was open
  useEffect(() => {
    if (state.isOpen && state.sessions.length === 0) {
      getSessions()
        .then((sessions) => dispatch({ type: 'SET_SESSIONS', payload: sessions }))
        .catch(() => dispatch({ type: 'SET_SESSIONS', payload: [] }));
    }
  // Only run on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectLogo = useCallback((key: LogoKey) => {
    setSelectedLogo(key);
    localStorage.setItem(LOGO_STORAGE_KEY, key);
  }, []);

  const handleTogglePrimaryColor = useCallback((value: boolean) => {
    setUsePrimaryColor(value);
    localStorage.setItem(PRIMARY_COLOR_STORAGE_KEY, String(value));
  }, []);

  const clearFabAttention = useCallback(() => {
    setShowBounce(false);
    setShowAutoTooltip(false);
  }, []);

  const handleToggle = useCallback(async () => {
    const newIsOpen = !state.isOpen;
    localStorage.setItem(IS_OPEN_KEY, String(newIsOpen));
    if (newIsOpen) clearFabAttention();
    dispatch({ type: 'TOGGLE_OPEN' });
    if (newIsOpen && state.sessions.length === 0) {
      getSessions()
        .then((sessions) => dispatch({ type: 'SET_SESSIONS', payload: sessions }))
        .catch(() => dispatch({ type: 'SET_SESSIONS', payload: [] }));
    }
  }, [state.isOpen, state.sessions.length, clearFabAttention]);

  const handleClose = useCallback(() => {
    localStorage.setItem(IS_OPEN_KEY, 'false');
    abort();
    dispatch({ type: 'CLOSE' });
  }, [abort]);

  const handleSend = useCallback(
    async (text: string) => {
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        sessionId: state.sessionId || '',
        role: 'user',
        content: text,
        createdAt: new Date().toISOString(),
      };
      dispatch({ type: 'ADD_USER_MESSAGE', payload: userMessage });

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        sessionId: state.sessionId || '',
        role: 'assistant',
        content: '',
        createdAt: new Date().toISOString(),
      };
      dispatch({ type: 'ADD_ASSISTANT_MESSAGE', payload: assistantMessage });

      await sendMessage(text, state.sessionId, (event: ChatStreamEvent) => {
        switch (event.type) {
          case 'token':
            dispatch({ type: 'SET_TOOL_CALL', payload: null });
            dispatch({ type: 'APPEND_TOKEN', payload: event.content });
            break;
          case 'segment_end':
            // LLM finished a thinking segment before calling a tool.
            // Next tokens will go into a new assistant bubble.
            dispatch({
              type: 'ADD_ASSISTANT_MESSAGE',
              payload: {
                id: `assistant-${Date.now()}`,
                sessionId: state.sessionId || '',
                role: 'assistant',
                content: '',
                createdAt: new Date().toISOString(),
              },
            });
            break;
          case 'tool_call':
            dispatch({
              type: 'SET_TOOL_CALL',
              payload: { name: event.name, label: event.label || 'Processing' },
            });
            break;
          case 'tool_result':
            // Keep tool call label visible until next token arrives or stream ends
            break;
          case 'done':
            dispatch({ type: 'SET_TOOL_CALL', payload: null });
            dispatch({ type: 'SET_SESSION_ID', payload: event.sessionId });
            // Clean up empty assistant bubbles from segment splits
            dispatch({ type: 'CLEANUP_EMPTY_ASSISTANTS' });
            if (event.fullResponse != null) {
              dispatch({ type: 'REPLACE_ASSISTANT_SEGMENTS', payload: event.fullResponse });
            }
            if (event.suggestedActions && event.suggestedActions.length > 0) {
              dispatch({ type: 'SET_QUICK_ACTIONS', payload: event.suggestedActions });
            }
            if (event.suggestFeedback) {
              dispatch({ type: 'SET_SUGGEST_FEEDBACK', payload: true });
            }
            getSessions()
              .then((sessions) =>
                dispatch({ type: 'SET_SESSIONS', payload: sessions }),
              )
              .catch(() => {});
            break;
          case 'error':
            dispatch({ type: 'SET_TOOL_CALL', payload: null });
            dispatch({
              type: 'APPEND_TOKEN',
              payload: `\n\nError: ${event.message}`,
            });
            break;
        }
      });
    },
    [state.sessionId, sendMessage],
  );

  const handleSelectSession = useCallback(async (sessionId: string) => {
    try {
      const session = await getSession(sessionId);
      dispatch({
        type: 'LOAD_SESSION',
        payload: {
          sessionId,
          messages: expandSegments(session.messages || []),
        },
      });
    } catch {
      dispatch({
        type: 'LOAD_SESSION',
        payload: { sessionId, messages: [] },
      });
    }
  }, []);

  const handleDeleteSession = useCallback(async (sessionId: string) => {
    try {
      await deleteSession(sessionId);
    } catch {
      // ignore
    }
    dispatch({ type: 'DELETE_SESSION', payload: sessionId });
  }, []);

  const handleNewChat = useCallback(() => {
    abort();
    dispatch({ type: 'NEW_CHAT' });
  }, [abort]);

  const [isRaisingTicket, setIsRaisingTicket] = useState(false);
  const handleRaiseTicket = useCallback(
    async (proposal: TicketProposal, attachments?: string[], notes?: string, captureScreenOnSubmit = true) => {
      setIsRaisingTicket(true);
      try {
        // Start with any user-added files. When the "capture my screen" option
        // is on (default), also grab a screenshot on submit and include it.
        const files = attachments && attachments.length ? [...attachments] : [];
        if (captureScreenOnSubmit) {
          const shot = await captureScreen();
          if (shot) files.push(shot);
        }
        const description = notes
          ? `${proposal.description}\n\nAdditional details from user:\n${notes}`
          : proposal.description;
        const res = await createSupportTicket({
          subject: proposal.subject,
          description,
          sessionId: state.sessionId,
          attachments: files,
          appendToTicketNumber: proposal.appendToTicketNumber,
        });
        // Appending a confirmation message makes it the new last message (no
        // proposal marker) → the card hides. The backend persists the same
        // message so it stays hidden after a reload too.
        const isAppend = Boolean(proposal.appendToTicketNumber);
        dispatch({
          type: 'ADD_ASSISTANT_MESSAGE',
          payload: {
            id: `ticket-${Date.now()}`,
            sessionId: state.sessionId || '',
            role: 'assistant',
            content: res.ticketNumber
              ? isAppend
                ? `✅ Added your note to ticket **#${res.ticketNumber}**. The support team will see it.`
                : `✅ Support ticket **#${res.ticketNumber}** has been created. The support team will follow up.`
              : isAppend
                ? '✅ Your note has been added to the support ticket.'
                : '✅ Your support ticket has been created. The support team will follow up.',
            createdAt: new Date().toISOString(),
          },
        });
      } catch (error) {
        // A refusal is permanent and names where the query has to go, so keep it
        // in the conversation like a confirmation. That also retires the proposal
        // card, which is derived from the last assistant message. Every other
        // failure may carry internal text, so it keeps the generic wording.
        if (error instanceof SupportTicketRefusedError) {
          dispatch({
            type: 'ADD_ASSISTANT_MESSAGE',
            payload: {
              id: `ticket-refused-${Date.now()}`,
              sessionId: state.sessionId || '',
              role: 'assistant',
              content: error.message,
              createdAt: new Date().toISOString(),
            },
          });
        } else {
          antdMessage.error(
            proposal.appendToTicketNumber
              ? 'Could not add your note to the ticket. Please try again.'
              : 'Could not create the support ticket. Please try again.',
          );
        }
      } finally {
        setIsRaisingTicket(false);
      }
    },
    [state.sessionId],
  );

  // The ticket proposal lives in the last assistant message (TICKET_PROPOSAL
  // HTML comment) so it survives reloads with no extra state. Dismiss is
  // in-memory only (keyed by message id).
  const [dismissedProposalId, setDismissedProposalId] = useState<string | null>(null);
  const lastAssistantMessage = state.messages.length
    ? [...state.messages].reverse().find((m) => m.role === 'assistant') || null
    : null;
  const ticketProposal: TicketProposal | null =
    !isStreaming &&
    lastAssistantMessage &&
    lastAssistantMessage.id !== dismissedProposalId
      ? parseTicketProposal(lastAssistantMessage.content)
      : null;

  const handleDismissTicket = useCallback(() => {
    if (lastAssistantMessage) setDismissedProposalId(lastAssistantMessage.id);
  }, [lastAssistantMessage]);

  const handleToggleSessionList = useCallback(async () => {
    if (!state.showSessionList && state.sessions.length === 0) {
      try {
        const sessions = await getSessions();
        dispatch({ type: 'SET_SESSIONS', payload: sessions });
      } catch {
        dispatch({ type: 'SET_SESSIONS', payload: [] });
      }
    }
    dispatch({ type: 'TOGGLE_SESSION_LIST' });
  }, [state.showSessionList, state.sessions.length]);

  // Listen for external "open chatbot and send message" events
  useEffect(() => {
    const handler = async (e: Event) => {
      const message = (e as CustomEvent<string>).detail;
      localStorage.setItem(IS_OPEN_KEY, 'true');
      clearFabAttention();
      dispatch({ type: 'OPEN' });
      if (state.sessions.length === 0) {
        getSessions()
          .then((sessions) => dispatch({ type: 'SET_SESSIONS', payload: sessions }))
          .catch(() => dispatch({ type: 'SET_SESSIONS', payload: [] }));
      }
      handleSend(message);
    };
    window.addEventListener('ai-chat:open-and-send', handler);
    return () => window.removeEventListener('ai-chat:open-and-send', handler);
  }, [handleSend, state.sessions.length, clearFabAttention]);

  if (!configLoaded) return null;

  // Determine the greeting to display: use streaming text if streaming, otherwise state.greeting
  const displayGreeting = (isGreetingStreaming || streamingGreetingText) ? streamingGreetingText : state.greeting;

  return (
    <>
      <style>{`
        ${logoStyles}
        @keyframes ai-fab-bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        .ai-fab-bounce { animation: ai-fab-bounce 1.5s ease-in-out infinite; }
      `}</style>
      {!state.isOpen && (selectedLogo !== 'default' ? (
        <div
          className={`${showBounce ? 'ai-fab-bounce' : ''}`}
          style={{
            position: 'fixed', right: 24, bottom: 24, zIndex: 1000,
            display: 'flex', alignItems: 'center', gap: 8,
          }}
        >
          {showAutoTooltip && (
            <div
              style={{
                background: 'rgba(0,0,0,0.75)',
                color: '#fff',
                padding: '6px 12px',
                borderRadius: 8,
                fontSize: 13,
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                marginRight: 4,
              }}
            >
              Hi! Need help?
            </div>
          )}
          <Tooltip title={<div style={{ textAlign: 'center', lineHeight: 1.3 }}><div style={{ fontWeight: 600 }}>{state.title}</div><div style={{ fontSize: 11, opacity: 0.85 }}>AI Assistant</div></div>} placement="top">
            <div
              role="button"
              tabIndex={0}
              onClick={handleToggle}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggle(); } }}
              style={{ cursor: 'pointer', width: 48, height: 48 }}
            >
              <LogoRenderer logoKey={selectedLogo} size={48} usePrimaryColor={usePrimaryColor} />
            </div>
          </Tooltip>
        </div>
      ) : (
        <div
          className={`${showBounce ? 'ai-fab-bounce' : ''}`}
          style={{
            position: 'fixed', right: 24, bottom: 24, zIndex: 1000,
            display: 'flex', alignItems: 'center', gap: 8,
          }}
        >
          {showAutoTooltip && (
            <div
              style={{
                background: 'rgba(0,0,0,0.75)',
                color: '#fff',
                padding: '6px 12px',
                borderRadius: 8,
                fontSize: 13,
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                marginRight: 4,
              }}
            >
              Hi! Need help?
            </div>
          )}
          <FloatButton
            icon={<LogoRenderer logoKey="default" size={29} />}
            type="primary"
            style={{ right: 0, bottom: 0, width: 48, height: 48, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)' }}
            onClick={handleToggle}
            tooltip={<div style={{ textAlign: 'center', lineHeight: 1.3 }}><div style={{ fontWeight: 600 }}>{state.title}</div><div style={{ fontSize: 11, opacity: 0.85 }}>AI Assistant</div></div>}
          />
        </div>
      ))}
      {state.isOpen && (
        <ChatWindow
          isVisible={state.isOpen}
          messages={state.messages}
          sessions={state.sessions}
          activeSessionId={state.sessionId}
          isStreaming={isStreaming}
          activeToolCall={state.activeToolCall}
          showSessionList={state.showSessionList}
          title={state.title}
          placeholder={state.placeholder}
          greeting={displayGreeting}
          isGreetingStreaming={isGreetingStreaming}
          suggestions={state.suggestions}
          quickActions={state.quickActions}
          suggestFeedback={state.suggestFeedback}
          ticketProposal={ticketProposal}
          isRaisingTicket={isRaisingTicket}
          onRaiseTicket={handleRaiseTicket}
          onDismissTicket={handleDismissTicket}
          selectedLogo={selectedLogo}
          usePrimaryColor={usePrimaryColor}
          onClose={handleClose}
          onSend={handleSend}
          onSelectSession={handleSelectSession}
          onDeleteSession={handleDeleteSession}
          onNewChat={handleNewChat}
          onToggleSessionList={handleToggleSessionList}
          onSelectLogo={handleSelectLogo}
          onTogglePrimaryColor={handleTogglePrimaryColor}
        />
      )}
    </>
  );
};

export default AiChatbot;
